import React, { useEffect, useRef } from 'react';
import { Command, X } from 'lucide-react';
import { GLOBAL_SHORTCUTS } from '../../hooks/useGlobalShortcuts';

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * Global keyboard-shortcuts modal — Wiz-parity help sheet.
 * Triggered via `?` (handled in useGlobalShortcuts), TopNav Help button,
 * or the `open-shortcuts` window event.
 *
 * R193-SHIP-BC a11y: focus is captured on open and restored on close.
 * Escape closes; Tab loops between the close button and the dialog body
 * (single focusable element so the loop is implicit).
 */
const KeyboardShortcutsModal: React.FC<Props> = ({ open, onClose }) => {
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const prevFocusRef = useRef<Element | null>(null);

  useEffect(() => {
    if (!open) return;
    prevFocusRef.current = document.activeElement;
    // Defer to next tick so the button is mounted before we focus it.
    const t = setTimeout(() => closeBtnRef.current?.focus(), 0);
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', handler);
    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', handler);
      const prev = prevFocusRef.current;
      if (prev && prev instanceof HTMLElement) {
        try { prev.focus(); } catch { /* element gone */ }
      }
    };
  }, [open, onClose]);

  if (!open) return null;

  const grouped = {
    global: GLOBAL_SHORTCUTS.filter(s => s.scope === 'global' || !s.scope),
    issues: GLOBAL_SHORTCUTS.filter(s => s.scope === 'issues'),
    inventory: GLOBAL_SHORTCUTS.filter(s => s.scope === 'inventory'),
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100]"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Keyboard shortcuts"
    >
      <div
        className="bg-wiz-card border border-wiz-border rounded-xl p-6 w-[420px] shadow-2xl max-h-[80vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white text-base font-semibold flex items-center gap-2">
            <Command className="w-4 h-4 text-teal-400" />
            Keyboard Shortcuts
          </h3>
          <button
            ref={closeBtnRef}
            onClick={onClose}
            className="text-gray-500 hover:text-white"
            aria-label="Close shortcuts"
          >
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>

        <div className="space-y-4">
          <ShortcutSection title="Global" items={grouped.global} />
          {grouped.issues.length > 0 && <ShortcutSection title="Issues / Lists" items={grouped.issues} />}
          {grouped.inventory.length > 0 && <ShortcutSection title="Inventory" items={grouped.inventory} />}
        </div>
      </div>
    </div>
  );
};

const ShortcutSection: React.FC<{
  title: string;
  items: { key: string; description: string }[];
}> = ({ title, items }) => (
  <div>
    <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">{title}</p>
    <div className="space-y-1.5">
      {items.map(s => (
        <div key={`${title}-${s.key}`} className="flex items-center justify-between">
          <span className="text-sm text-gray-400">{s.description}</span>
          <kbd className="px-2 py-0.5 bg-wiz-darker border border-wiz-border rounded text-xs text-gray-300 font-mono min-w-[36px] text-center">
            {s.key}
          </kbd>
        </div>
      ))}
    </div>
  </div>
);

export default KeyboardShortcutsModal;
